import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/admin/AdminLayout";
import KPICard from "@/components/admin/KPICard";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import {
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, X, ArrowLeft, DollarSign, Filter, Download, Search, CheckCircle, XCircle, Eye } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Payout {
  id: string;
  user_id: string;
  user_email?: string;
  user_name?: string;
  amount: number;
  status: string;
  bank_details: any;
  notes: string;
  requested_at: string;
  processed_at: string;
  processed_by: string;
  reference_number?: string;
  earning_sources?: {
    affiliate: number;
    creator: number;
  };
}

export default function AdminPayouts() {
  const { isAdmin, isLoading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [filteredPayouts, setFilteredPayouts] = useState<Payout[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  const [requestedDateFrom, setRequestedDateFrom] = useState("");
  const [requestedDateTo, setRequestedDateTo] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState<{start: string; end: string}>({
    start: "",
    end: ""
  });
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [processingNotes, setProcessingNotes] = useState("");
  const [stats, setStats] = useState({
    pendingPayouts: 0,
    pendingAmount: 0,
    processedThisMonth: 0,
    totalProcessed: 0,
  });

  useEffect(() => {
    if (!authLoading && !isAdmin()) {
      navigate("/");
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    loadPayouts();
  }, []);

  useEffect(() => {
    filterPayouts();
  }, [searchQuery, statusFilter, dateRangeFilter, payouts]);

  const loadPayouts = async () => {
    try {
      setLoading(true);

      // 1. Fetch payouts
      const { data: payoutsData, error: payoutsError } = await supabase
        .from("payouts")
        .select("*")
        .order("requested_at", { ascending: false });

      if (payoutsError) throw payoutsError;

      // 2. Get unique user IDs
      const userIds = [...new Set(payoutsData?.map(p => p.user_id) || [])];

      // 3. Fetch user data via edge function
      const { data: usersData, error: usersError } = await supabase.functions.invoke(
        "get-users-admin",
        { body: { userIds } }
      );

      if (usersError) console.error("Error fetching users:", usersError);

      // 4. Fetch earning sources for each user
      const earningSourcesMap: Record<string, { affiliate: number; creator: number }> = {};
      
      for (const userId of userIds) {
        const { data: ledger } = await supabase
          .from("earnings_ledger")
          .select("transaction_type, amount")
          .eq("user_id", userId);

        const affiliate = ledger
          ?.filter(tx => tx.transaction_type === 'affiliate_commission')
          .reduce((sum, tx) => sum + parseFloat(tx.amount.toString()), 0) || 0;

        const creator = ledger
          ?.filter(tx => tx.transaction_type === 'creator_reward')
          .reduce((sum, tx) => sum + parseFloat(tx.amount.toString()), 0) || 0;

        earningSourcesMap[userId] = { affiliate, creator };
      }

      // 5. Map user data to payouts
      const enrichedPayouts = payoutsData?.map(payout => {
        const user = usersData?.users?.find((u: any) => u.id === payout.user_id);
        return {
          ...payout,
          user_email: user?.email || "N/A",
          user_name: user?.user_metadata?.first_name 
            ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`
            : t("admin.payouts.fallback.defaultUser"),
          earning_sources: earningSourcesMap[payout.user_id]
        };
      }) || [];

      setPayouts(enrichedPayouts);
      calculateStats(enrichedPayouts);
    } catch (error) {
      console.error("Error loading payouts:", error);
        toast({
          title: t("admin.payouts.messages.error"),
          description: t("admin.payouts.messages.loadError"),
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: Payout[]) => {
    const pending = data.filter((p) => p.status === "pending");
    const pendingAmount = pending.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const thisMonth = data.filter(
      (p) =>
        p.status === "completed" &&
        p.processed_at &&
        new Date(p.processed_at).getMonth() === new Date().getMonth()
    );
    const processedAmount = thisMonth.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const totalAmount = data
      .filter((p) => p.status === "completed")
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);

    setStats({
      pendingPayouts: pending.length,
      pendingAmount,
      processedThisMonth: processedAmount,
      totalProcessed: totalAmount,
    });
  };

  const filterPayouts = () => {
    let filtered = payouts;

    // Filtro per status
    if (statusFilter !== "all") {
      filtered = filtered.filter((p) => p.status === statusFilter);
    }

    // Filtro per range di date
    if (dateRangeFilter.start) {
      filtered = filtered.filter((p) => 
        new Date(p.requested_at) >= new Date(dateRangeFilter.start)
      );
    }
    if (dateRangeFilter.end) {
      filtered = filtered.filter((p) => 
        new Date(p.requested_at) <= new Date(dateRangeFilter.end + "T23:59:59")
      );
    }

    // Filtro per ricerca (utente email/nome)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((p) =>
        p.user_email?.toLowerCase().includes(query) ||
        p.user_name?.toLowerCase().includes(query) ||
        p.id.toLowerCase().includes(query)
      );
    }

    setFilteredPayouts(filtered);
  };

  const toggleFilters = () => {
    setIsFiltersOpen(!isFiltersOpen);
  };

  const applyFilters = () => {
    filterPayouts();
  };

  const resetFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setRequestedDateFrom("");
    setRequestedDateTo("");
    setDateRangeFilter({ start: "", end: "" });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (statusFilter !== "all") count++;
    if (dateRangeFilter.start || dateRangeFilter.end) count++;
    return count;
  };

  const exportData = () => {
    if (filteredPayouts.length === 0) {
      toast({
        title: t("admin.payouts.messages.noData"),
        description: t("admin.payouts.messages.noPayoutsToExport"),
        variant: "warning",
      });
      return;
    }

    try {
      const headers = [
        t("admin.payouts.export.userId"),
        t("admin.payouts.export.amount"),
        t("admin.payouts.export.iban"),
        t("admin.payouts.export.requestDate"),
        t("admin.payouts.export.status")
      ];
      const rows = filteredPayouts.map((p) => [
        p.user_id,
        Number(p.amount || 0).toFixed(2),
        p.bank_details?.iban || 'N/A',
        format(new Date(p.requested_at), "dd/MM/yyyy HH:mm", { locale: it }),
        p.status,
      ]);

      const escapeCsvCell = (cell: string) => `"${cell.replace(/"/g, '""')}"`;
      
      const csvContent = [
        headers.map(escapeCsvCell).join(','),
        ...rows.map(row => row.map(cell => escapeCsvCell(String(cell))).join(','))
      ].join('\n');

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `payouts_${format(new Date(), "yyyy-MM-dd_HH-mm")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      toast({
        title: t("admin.payouts.messages.exportCompleted"),
        description: t("admin.payouts.messages.csvDownloaded", { count: filteredPayouts.length }),
      });
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast({
        title: t("admin.payouts.messages.error"),
        description: t("admin.payouts.messages.exportError"),
        variant: "error",
      });
    }
  };

  const openProcessDialog = (payout: Payout) => {
    setSelectedPayout(payout);
    setProcessingNotes("");
    setProcessDialogOpen(true);
  };

  const processPayout = async (approve: boolean) => {
    if (!selectedPayout || !user) return;

    try {
      const { error } = await supabase
        .from("payouts")
        .update({
          status: approve ? "completed" : "rejected",
          processed_at: new Date().toISOString(),
          processed_by: user.id,
          notes: processingNotes || null,
        })
        .eq("id", selectedPayout.id);

      if (error) throw error;

      // If approved, insert negative record in earnings_ledger and update affiliate_tracking
      if (approve) {
        // Get last balance for user
        const { data: lastBalance } = await supabase
          .from('earnings_ledger')
          .select('balance_after')
          .eq('user_id', selectedPayout.user_id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        const currentBalance = parseFloat(lastBalance?.balance_after?.toString() || '0');
        const newBalance = currentBalance - selectedPayout.amount;

        // Insert payout record in earnings_ledger (negative amount)
        await supabase
          .from('earnings_ledger')
          .insert({
            user_id: selectedPayout.user_id,
            transaction_type: 'payout',
            amount: -selectedPayout.amount,
            balance_after: newBalance,
            reference_type: 'payout',
            reference_id: selectedPayout.id,
            description: `Payout bonifico bancario`
          });

        // Reset pending_payout in affiliate_tracking
        await supabase
          .from('affiliate_tracking')
          .update({ pending_payout: 0 })
          .eq('user_id', selectedPayout.user_id);
      }

      // Invia notifica email
      await supabase.functions.invoke("notify-payout-processed", {
        body: {
          payoutId: selectedPayout.id,
          status: approve ? "completed" : "rejected",
          adminNotes: processingNotes
        }
      });

      toast({
        title: t("admin.payouts.messages.success"),
        description: approve ? t("admin.payouts.messages.payoutApproved") : t("admin.payouts.messages.payoutRejected"),
      });

      loadPayouts();
      setProcessDialogOpen(false);
      setSelectedPayout(null);
      setProcessingNotes("");
    } catch (error) {
      console.error("Error processing payout:", error);
        toast({
          title: t("admin.payouts.messages.error"),
          description: t("admin.payouts.messages.processError"),
        variant: "error",
      });
    }
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
            <BreadcrumbLink href="/dashboard">{t("common.dashboard")}</BreadcrumbLink>
            <BreadcrumbSeparator />
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbPage>{t("admin.payouts.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-8">
          <BrutalButton
            variant="outline"
            onClick={() => navigate("/dashboard")}
            aria-label={t("admin.payouts.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </BrutalButton>
          
          <h1 className="text-3xl font-heading font-bold">{t("admin.payouts.title")}</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard title={t("admin.payouts.kpi.pendingRequests")} value={stats.pendingPayouts} />
          <KPICard title={t("admin.payouts.kpi.pendingAmount")} value={`€${stats.pendingAmount.toFixed(2)}`} />
          <KPICard title={t("admin.payouts.kpi.processedThisMonth")} value={`€${stats.processedThisMonth.toFixed(2)}`} />
          <KPICard title={t("admin.payouts.kpi.totalProcessed")} value={`€${stats.totalProcessed.toFixed(2)}`} />
        </div>

        {/* Filters */}
        <BrutalCard>
          <div className="space-y-4">
            {/* Riga principale sempre visibile */}
            <div className="flex gap-4 items-center">
              {/* Barra ricerca estesa */}
              <div className="flex-1">
                <BrutalInput
                  placeholder={t("admin.payouts.filters.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              {/* Bottone filtri con badge count */}
              <button
                onClick={toggleFilters}
                className="relative p-3 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                aria-label={t("admin.payouts.filters.advancedFiltersLabel")}
              >
                <Filter className="w-5 h-5" />
                {getActiveFiltersCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-dark font-bold text-xs w-5 h-5 rounded-full flex items-center justify-center border-[2px] border-dark">
                    {getActiveFiltersCount()}
                  </span>
                )}
              </button>

              {/* Bottone Export CSV */}
              <button
                onClick={exportData}
                className="p-3 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                aria-label={t("admin.payouts.filters.exportCsvLabel")}
                title={t("admin.payouts.filters.exportCsvTitle")}
              >
                <Download className="w-5 h-5" />
              </button>
            </div>

            {/* Pannello filtri espandibile */}
            {isFiltersOpen && (
              <div className="space-y-4 pt-4 border-t-[3px] border-dark">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Select Status */}
                  <div>
              <label className="text-sm font-bold mb-2 block">{t("admin.payouts.filters.statusLabel")}</label>
              <select 
                className="w-full px-4 py-2 border-4 border-brutal-black rounded-none bg-white"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{t("admin.payouts.filters.allStatuses")}</option>
                <option value="pending">{t("admin.payouts.filters.pending")}</option>
                <option value="completed">{t("admin.payouts.filters.completed")}</option>
                <option value="rejected">{t("admin.payouts.filters.rejected")}</option>
                    </select>
                  </div>

                </div>

                {/* Filtri Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
              <label className="text-sm font-bold mb-2 block">{t("admin.payouts.filters.requestedFromLabel")}</label>
              <BrutalInput 
                type="date"
                value={requestedDateFrom}
                onChange={(e) => setRequestedDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-bold mb-2 block">{t("admin.payouts.filters.requestedToLabel")}</label>
              <BrutalInput 
                type="date"
                value={requestedDateTo}
                onChange={(e) => setRequestedDateTo(e.target.value)}
              />
            </div>
                </div>

                {/* Bottoni azione */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={resetFilters}
                    className="px-6 py-3 border-[3px] border-dark font-heading font-bold uppercase tracking-wider bg-card text-foreground shadow-[6px_6px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px] hover:shadow-dark transition-all"
                  >
                    Reset
                  </button>
                  <button
                    onClick={applyFilters}
                    className="px-6 py-3 border-[3px] border-dark font-heading font-bold uppercase tracking-wider bg-primary text-dark shadow-[6px_6px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px] hover:shadow-dark transition-all"
                  >
                    Applica
                  </button>
                </div>
              </div>
            )}
          </div>
        </BrutalCard>

        {/* Payouts Table */}
        <BrutalCard>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface border-b-[3px] border-dark">
                <tr>
                <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.payouts.table.requestDate")}</th>
                <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.payouts.table.user")}</th>
                <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.payouts.table.email")}</th>
                <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.payouts.table.amount")}</th>
                <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.payouts.table.earningSources")}</th>
                <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.payouts.table.status")}</th>
                <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.payouts.table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayouts.length === 0 ? (
                  <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">
                  {t("admin.payouts.messages.noPayoutsFound")}
                </td>
                  </tr>
                ) : (
                  filteredPayouts.map((payout) => (
                    <tr
                      key={payout.id}
                      className="border-b-[3px] border-dark hover:bg-surface"
                    >
                      <td className="px-6 py-4">
                        {format(new Date(payout.requested_at), "dd/MM/yyyy HH:mm", { locale: it })}
                      </td>
                      <td className="px-6 py-4 font-medium">
                        {payout.user_name}
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {payout.user_email}
                      </td>
                      <td className="px-6 py-4 font-bold">
                        €{Number(payout.amount || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {payout.earning_sources && (
                            <>
                              {payout.earning_sources.affiliate > 0 && (
                                <div className="flex items-center gap-1">
                                  <BrutalBadge variant="default" className="text-[10px] px-1 py-0">
                                    AFF
                                  </BrutalBadge>
                                  <span className="text-xs">€{payout.earning_sources.affiliate.toFixed(2)}</span>
                                </div>
                              )}
                              {payout.earning_sources.creator > 0 && (
                                <div className="flex items-center gap-1">
                                  <BrutalBadge variant="success" className="text-[10px] px-1 py-0">
                                    CREATE
                                  </BrutalBadge>
                                  <span className="text-xs">€{payout.earning_sources.creator.toFixed(2)}</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          variant={
                            payout.status === "pending"
                              ? "secondary"
                              : payout.status === "completed"
                              ? "default"
                              : "destructive"
                          }
                          className="uppercase"
                        >
                          {payout.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        {payout.status === "pending" && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => openProcessDialog(payout)}
                                  className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                  aria-label={t("admin.payouts.buttons.processTooltip")}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t("admin.payouts.buttons.processTooltip")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </BrutalCard>
      </div>

      <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
        <DialogContent>
          <DialogHeader>
          <DialogTitle>{t("admin.payouts.dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("admin.payouts.dialog.description")}
          </DialogDescription>
          </DialogHeader>

          {selectedPayout && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-bold">{t("admin.payouts.dialog.amount")}:</span> €{Number(selectedPayout.amount || 0).toFixed(2)}
                </div>
                <div>
                  <span className="font-bold">{t("admin.payouts.dialog.type")}:</span> Bonifico bancario
                </div>
              </div>

              {/* Bank Details Section */}
              {selectedPayout.bank_details && (
                <div className="bg-surface border-[3px] border-dark p-4 space-y-3">
                  <h4 className="font-bold text-sm uppercase tracking-wider border-b-[2px] border-dark pb-2">
                    {t("admin.payouts.dialog.bankDetails")}
                  </h4>
                  
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-bold">{t("admin.payouts.dialog.accountHolder")}:</span>
                      <span className="font-mono">{selectedPayout.bank_details.account_holder || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">{t("admin.payouts.dialog.iban")}:</span>
                      <span className="font-mono text-xs select-all cursor-pointer" title="Click to copy">
                        {selectedPayout.bank_details.iban || 'N/A'}
                      </span>
                    </div>
                    {selectedPayout.bank_details.bic_swift && (
                      <div className="flex justify-between">
                        <span className="font-bold">{t("admin.payouts.dialog.bicSwift")}:</span>
                        <span className="font-mono">{selectedPayout.bank_details.bic_swift}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="font-bold">{t("admin.payouts.dialog.fiscalCode")}:</span>
                      <span className="font-mono">{selectedPayout.bank_details.fiscal_code || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}


              <div>
                <label className="text-sm font-bold mb-2 block">{t("admin.payouts.dialog.notesLabel")}</label>
                <BrutalTextarea 
                  placeholder={t("admin.payouts.dialog.notesPlaceholder")}
                  value={processingNotes}
                  onChange={(e) => setProcessingNotes(e.target.value)}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <BrutalButton variant="outline" onClick={() => processPayout(false)}>
              <X className="w-4 h-4 mr-2" />
              {t("admin.payouts.buttons.reject")}
            </BrutalButton>
            <BrutalButton onClick={() => processPayout(true)}>
              <CheckCircle className="w-4 h-4 mr-2" />
              {t("admin.payouts.buttons.approve")}
            </BrutalButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
